/**
 * API service for fetching coupon data via Cloud Function
 * This uses the /getCouponData endpoint which verifies ownership via token
 * without requiring Firebase Auth entries
 */

const getBaseFunctionsUrl = () => {
    // For development with emulator - update the IP address here (e.g., 192.168.1.195 or 127.0.0.1)
    if (process.env.NODE_ENV === 'development' || import.meta.env.DEV) {
        return 'http://192.168.1.195:5001';
    }

    // For production
    return 'https://sendcouponemail-5iiwpornyq-uc.a.run.app';
};

export type GetCouponDataParams = {
    token: string;
    couponId: string;
    clientID: string;
};

export type GetCouponDataResponse = {
    success: boolean;
    coupon?: {
        id: string;
        playerId: string;
        offerId: string;
        offerName: string;
        issuedAt: string; // ISO string
        validUntil: string; // ISO string
        gameId: string;
        obtainedScore: number;
        leaderboardScope: 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all-time';
        status: 'sent' | 'opened' | 'validated';
        clientName?: string;
    };
    offer?: {
        id: string;
        title?: string;
        shopName?: string;
        shopPlace?: string;
        createdAt?: string;
        updatedAt?: string;
        [key: string]: any;
    };
    message?: string;
    error?: string;
};

export const CouponCloudFunctionAPI = {
    /**
     * Fetch coupon data using coupon token authentication
     * This endpoint uses the coupon token to verify ownership without creating Firebase Auth entries
     */
    async getCouponData(params: GetCouponDataParams): Promise<GetCouponDataResponse> {
        const url = `${getBaseFunctionsUrl()}/getCouponData`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(params),
            });

            // Check if response is JSON
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response:', text.substring(0, 200));
                return {
                    success: false,
                    message: `Invalid server response (${response.status})`,
                    error: `Expected JSON, got ${contentType}`,
                };
            }

            const result = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    message: result.message || 'Failed to fetch coupon data',
                    error: result.error,
                };
            }

            return result;
        } catch (error: any) {
            console.error('Error fetching coupon data:', error);
            return {
                success: false,
                message: 'Connection error while fetching coupon data',
                error: error.message,
            };
        }
    },

    /**
     * Validate coupon using coupon token authentication
     * This endpoint uses the coupon token to verify ownership before updating status
     */
    async validateCoupon(params: GetCouponDataParams): Promise<{ success: boolean; message?: string; error?: string }> {
        const url = `${getBaseFunctionsUrl()}/validateCoupon`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...params,
                    status: 'validated',
                }),
            });

            // Check if response is JSON
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response:', text.substring(0, 200));
                return {
                    success: false,
                    message: `Invalid server response (${response.status})`,
                    error: `Expected JSON, got ${contentType}`,
                };
            }

            const result = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    message: result.message || 'Failed to validate coupon',
                    error: result.error,
                };
            }

            return result;
        } catch (error: any) {
            console.error('Error validating coupon:', error);
            return {
                success: false,
                message: 'Connection error while validating coupon',
                error: error.message,
            };
        }
    },
};
